// Organisation singleton — name, address, logos. Two consumers fetch
// from here: the admin Settings UI (which can also write), and the
// chrome / report-preview surfaces that read the logo and address.
//
// Two read shapes:
//   * ``useOrganisation()``       — authenticated, full payload
//   * ``useOrganisationPublic()`` — anonymous, login-page slice only
//
// All write hooks invalidate the relevant query so other consumers
// (header, login, report preview) pick up the change without a page
// reload.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useEffect } from "react";

import { setDisplayTimeZone } from "@/utils/dateFormat";

import { apiClient, publicApiClient } from "./client";

export interface Organisation {
  id: string;
  name: string;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string | null;
  postcode: string;
  country: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  primary_logo_url: string | null;
  primary_logo_mime: string | null;
  watermark_logo_url: string | null;
  watermark_logo_mime: string | null;
  report_header_logo_url: string | null;
  report_header_logo_mime: string | null;
  footer_graphic_url: string | null;
  footer_graphic_mime: string | null;
  /** Reporting identity printed on every report: the radiologist's name +
   *  post-nominals in the footer, the tagline under the masthead logo.
   *  NULL falls back to the deployment's env value, so clearing a box
   *  restores the previous value rather than blanking the report. */
  radiologist_name: string | null;
  radiologist_certifications: string | null;
  tagline: string | null;
  /** Report-ID prefix for the Smart Report scheme: the report ID shown is
   *  ``{prefix}-{accession-base}`` (accession minus its modality suffix),
   *  or just the base when unset. */
  report_id_prefix: string | null;
  /** IANA timezone for the clinic (e.g. "Australia/Brisbane"). DISPLAY
   *  ONLY — timestamps are stored in UTC and converted at render time.
   *  Does NOT drive the on-prem agent, which reads its own
   *  SONOWS_AUTO_SYNC_TIMEZONE from the agent .env. */
  timezone: string | null;
  /** Workflow setting — when true, sonographers can manually edit a
   *  worksheet's clinical priority via the worksheet form. */
  allow_manual_priority_change: boolean;
  /** Workflow setting — when true, sonographers can draft + sign their
   *  own report, which is what arms the HL7 dispatch to the RIS. Needed
   *  for RIS deployments where the radiologist never logs in here; must
   *  stay false for independent clinics where the radiologist signs. */
  allow_sonographer_sign: boolean;
  updated_at: string;
}

export interface OrganisationPublic {
  name: string;
  primary_logo_url: string | null;
}

export interface OrganisationPatch {
  name?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  radiologist_name?: string | null;
  radiologist_certifications?: string | null;
  tagline?: string | null;
  report_id_prefix?: string | null;
  timezone?: string | null;
  allow_manual_priority_change?: boolean;
  allow_sonographer_sign?: boolean;
}

export type LogoType =
  | "primary"
  | "watermark"
  | "report_header"
  | "footer_graphic";

const BASE = "/organisation";

export const ORG_KEY = ["organisation"] as const;
export const ORG_PUBLIC_KEY = ["organisation", "public"] as const;

export async function getOrganisation(): Promise<Organisation> {
  const { data } = await apiClient.get<Organisation>(BASE);
  return data;
}

export async function getOrganisationPublic(): Promise<OrganisationPublic> {
  const { data } = await publicApiClient.get<OrganisationPublic>(
    `${BASE}/public`,
  );
  return data;
}

export async function patchOrganisation(
  patch: OrganisationPatch,
): Promise<Organisation> {
  const { data } = await apiClient.patch<Organisation>(BASE, patch);
  return data;
}

export async function uploadLogo(
  type: LogoType,
  file: Blob,
  filename: string,
): Promise<Organisation> {
  const form = new FormData();
  form.append("file", file, filename);
  form.append("type", type);
  const { data } = await apiClient.post<Organisation>(`${BASE}/logo`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteLogo(type: LogoType): Promise<Organisation> {
  const { data } = await apiClient.delete<Organisation>(`${BASE}/logo`, {
    params: { type },
  });
  return data;
}

export function useOrganisation(): UseQueryResult<Organisation> {
  const query = useQuery({
    queryKey: ORG_KEY,
    queryFn: getOrganisation,
    staleTime: 60_000,
  });
  // Publish the clinic's timezone to the date formatters as soon as the
  // organisation is known. Doing it here (rather than in one app-shell
  // component) means EVERY surface that reads the org — worklist,
  // worksheet, reports, admin — arms the formatters, so no page can
  // render timestamps in the browser's clock just because it forgot to
  // opt in. ``setDisplayTimeZone`` is idempotent and ignores unknown
  // zones, so repeated calls during refetch are free.
  useEffect(() => {
    if (query.data) setDisplayTimeZone(query.data.timezone);
  }, [query.data]);
  return query;
}

export function useOrganisationPublic(): UseQueryResult<OrganisationPublic> {
  return useQuery({
    queryKey: ORG_PUBLIC_KEY,
    queryFn: getOrganisationPublic,
    staleTime: 60_000,
  });
}

export function usePatchOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: patchOrganisation,
    onSuccess: (org) => {
      qc.setQueryData(ORG_KEY, org);
      qc.invalidateQueries({ queryKey: ORG_PUBLIC_KEY });
    },
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      file,
      filename,
    }: {
      type: LogoType;
      file: Blob;
      filename: string;
    }) => uploadLogo(type, file, filename),
    onSuccess: (org) => {
      qc.setQueryData(ORG_KEY, org);
      qc.invalidateQueries({ queryKey: ORG_PUBLIC_KEY });
    },
  });
}

export function useDeleteLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: LogoType) => deleteLogo(type),
    onSuccess: (org) => {
      qc.setQueryData(ORG_KEY, org);
      qc.invalidateQueries({ queryKey: ORG_PUBLIC_KEY });
    },
  });
}
